import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  useGetCompanySurveysQuery,
  useGetMySurveySubmissionsQuery,
  useStartSurveySubmissionMutation,
  useGetSurveySubmissionByIdQuery,
  useUpdateSurveyAnswerMutation,
  useSubmitSurveyMutation,
} from "../../services/api";
import QuestionRenderer from "../Diagnosticpage/components/QuestionRenderer";
import "./Surveys.css";

/**
 * Employee Surveys Page
 * Three views: list (default), fill (question-by-question), completed
 */
export default function Surveys() {
  const [view, setView] = useState("list"); // 'list' | 'fill' | 'completed'
  const [activeSubmissionId, setActiveSubmissionId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const user = useSelector((state) => state.auth.user);
  const companyId = user?.company?._id || user?.company;

  // ==================== API QUERIES ====================
  const { data: surveys = [], isLoading: surveysLoading } = useGetCompanySurveysQuery(
    companyId,
    { skip: !companyId, refetchOnMountOrArgChange: true }
  );

  const { data: mySubmissions = [], refetch: refetchSubmissions } = useGetMySurveySubmissionsQuery(
    undefined,
    { refetchOnMountOrArgChange: true }
  );

  const { data: activeSubmission, refetch: refetchActive } = useGetSurveySubmissionByIdQuery(
    activeSubmissionId,
    { skip: !activeSubmissionId }
  );

  // ==================== MUTATIONS ====================
  const [startSubmission] = useStartSurveySubmissionMutation();
  const [updateAnswer] = useUpdateSurveyAnswerMutation();
  const [submitSurvey] = useSubmitSurveyMutation();

  // ==================== HELPERS ====================
  const getSubmissionForSurvey = useCallback(
    (templateId) => {
      return mySubmissions.find(
        (s) => (s.template?._id || s.template) === templateId
      );
    },
    [mySubmissions]
  );

  const getSurveyStatus = useCallback(
    (templateId) => {
      const submission = getSubmissionForSurvey(templateId);
      if (!submission) return "not_started";
      return submission.status; // 'draft' or 'submitted'
    },
    [getSubmissionForSurvey]
  );

  // ==================== HANDLERS ====================
  const handleStartSurvey = async (templateId) => {
    try {
      const result = await startSubmission({ templateId }).unwrap();
      const submissionId = result.submission?._id || result.submission;
      setActiveSubmissionId(submissionId);
      setCurrentQuestionIndex(0);
      setView("fill");
      refetchSubmissions();
    } catch (error) {
      console.error("Failed to start survey:", error);
      alert(error?.data?.message || "Failed to start survey");
    }
  };

  const handleContinueSurvey = (submission) => {
    setActiveSubmissionId(submission._id);
    setCurrentQuestionIndex(submission.currentQuestionIndex || 0);
    setView("fill");
  };

  const handleAnswerChange = async (value, valueLabel, valueLabels) => {
    if (!activeSubmissionId || !activeSubmission) return;

    try {
      await updateAnswer({
        id: activeSubmissionId,
        questionIndex: currentQuestionIndex,
        value,
        valueLabel: valueLabel || undefined,
        valueLabels: valueLabels || undefined,
      }).unwrap();
      refetchActive();
    } catch (error) {
      console.error("Failed to save answer:", error);
    }
  };

  const handleNext = () => {
    if (!activeSubmission) return;
    const totalQuestions = activeSubmission.answers?.length || 0;
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!activeSubmissionId) return;
    try {
      await submitSurvey(activeSubmissionId).unwrap();
      refetchSubmissions();
      setView("completed");
    } catch (error) {
      console.error("Failed to submit survey:", error);
      alert(error?.data?.message || "Failed to submit survey");
    }
  };

  const handleBackToList = () => {
    setView("list");
    setActiveSubmissionId(null);
    setCurrentQuestionIndex(0);
    refetchSubmissions();
  };

  // ==================== LIST VIEW ====================
  if (view === "list") {
    return (
      <div className="survey-container">
        <h1 className="survey-title">Surveys</h1>
        <p className="survey-subtitle">Complete surveys assigned to your company</p>

        {surveysLoading && <div className="survey-loading">Loading surveys...</div>}

        {!surveysLoading && surveys.length === 0 && (
          <div className="survey-empty">
            <div className="survey-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              </svg>
            </div>
            <p>No surveys available at this time.</p>
          </div>
        )}

        {!surveysLoading && surveys.length > 0 && (
          <div className="survey-list">
            {surveys.map((survey) => {
              const status = getSurveyStatus(survey._id);
              const submission = getSubmissionForSurvey(survey._id);

              return (
                <div key={survey._id} className="survey-card">
                  <div className="survey-card-header">
                    <h3 className="survey-card-title">{survey.title}</h3>
                    <span className={`survey-status-badge survey-status-${status}`}>
                      {status === "not_started" && "Not Started"}
                      {status === "draft" && "In Progress"}
                      {status === "submitted" && "Completed"}
                    </span>
                  </div>

                  {survey.description && (
                    <p className="survey-card-desc">{survey.description}</p>
                  )}

                  <div className="survey-card-meta">
                    <span>{survey.questions?.length || 0} questions</span>
                    {submission?.completionPercentage > 0 && status === "draft" && (
                      <span>{submission.completionPercentage}% complete</span>
                    )}
                  </div>

                  <div className="survey-card-actions">
                    {status === "not_started" && (
                      <button
                        className="survey-btn survey-btn-primary"
                        onClick={() => handleStartSurvey(survey._id)}
                      >
                        Start Survey
                      </button>
                    )}
                    {status === "draft" && submission && (
                      <button
                        className="survey-btn survey-btn-primary"
                        onClick={() => handleContinueSurvey(submission)}
                      >
                        Continue
                      </button>
                    )}
                    {status === "submitted" && (
                      <button className="survey-btn survey-btn-disabled" disabled>
                        Completed
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ==================== FILL VIEW ====================
  if (view === "fill" && activeSubmission) {
    const totalQuestions = activeSubmission.answers?.length || 0;
    const currentAnswer = activeSubmission.answers?.[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    // Build question object for QuestionRenderer
    const templateQuestions = activeSubmission.template?.questions || [];
    const templateQuestion = templateQuestions[currentQuestionIndex]?.inlineQuestion || {};

    const question = {
      _id: currentAnswer?._id,
      text: currentAnswer?.questionText || templateQuestion.text,
      type: currentAnswer?.questionType || templateQuestion.type,
      questionType: currentAnswer?.questionType || templateQuestion.type,
      options: templateQuestion.options || [],
      ratingConfig: templateQuestion.ratingConfig || { min: 1, max: 5 },
    };

    const progress = totalQuestions > 0
      ? Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)
      : 0;

    return (
      <div className="survey-container">
        <button className="survey-back-btn" onClick={handleBackToList}>
          &larr; Back to Surveys
        </button>

        <div className="survey-fill-card">
          {/* Header */}
          <div className="survey-fill-header">
            <h2 className="survey-fill-title">{activeSubmission.templateTitle}</h2>
            {activeSubmission.template?.instructions && (
              <p className="survey-fill-instructions">{activeSubmission.template.instructions}</p>
            )}
          </div>

          {/* Progress bar */}
          <div className="survey-progress">
            <div className="survey-progress-bar">
              <div className="survey-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="survey-progress-text">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
          </div>

          {/* Question */}
          <div className="survey-question">
            <div className="survey-question-text">
              <span className="survey-question-number">Q{currentQuestionIndex + 1}.</span>
              {currentAnswer?.questionText}
            </div>

            <div className="survey-question-input">
              <QuestionRenderer
                question={question}
                value={currentAnswer?.value}
                onChange={handleAnswerChange}
                disabled={false}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="survey-nav">
            <button
              className="survey-btn survey-btn-secondary"
              onClick={handlePrev}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </button>

            {isLastQuestion ? (
              <button
                className="survey-btn survey-btn-primary"
                onClick={handleSubmit}
              >
                Submit Survey
              </button>
            ) : (
              <button
                className="survey-btn survey-btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== COMPLETED VIEW ====================
  if (view === "completed") {
    return (
      <div className="survey-container">
        <div className="survey-completed">
          <div className="survey-completed-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h2 className="survey-completed-title">Survey Submitted!</h2>
          <p className="survey-completed-text">
            Thank you for completing the survey. Your responses have been recorded.
          </p>
          <button className="survey-btn survey-btn-primary" onClick={handleBackToList}>
            Back to Surveys
          </button>
        </div>
      </div>
    );
  }

  // Loading/fallback
  return (
    <div className="survey-container">
      <div className="survey-loading">Loading...</div>
    </div>
  );
}
